package web.fstech.onthecoffee

import androidx.compose.foundation.layout.*
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.*
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.unit.dp
import kotlinx.browser.window
import web.fstech.onthecoffee.privacy.AppLanguage
import web.fstech.onthecoffee.resources.AppStrings

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun LandingPage(
    onNavigate: (String) -> Unit,
    language: AppLanguage = AppLanguage.ENGLISH,
    onLanguageChange: (AppLanguage) -> Unit = {}
) {
    val scrollState = rememberScrollState()
    val strings = AppStrings.get(language)

    Scaffold(
        topBar = {
            TopAppBar(
                title = { Text(strings.appTitle) },
                actions = {
                    Row(
                        verticalAlignment = Alignment.CenterVertically,
                        horizontalArrangement = Arrangement.spacedBy(6.dp),
                        modifier = Modifier.padding(end = 8.dp)
                    ) {
                        TextButton(onClick = { onNavigate("signin") }) { Text(strings.navSignIn) }
                        TextButton(onClick = { onNavigate("signup") }) { Text(strings.navSignUp) }
                        TextButton(onClick = { onNavigate("privacy") }) { Text(strings.navPrivacyPolicy) }

                        // Language Toggle Buttons at the Top
                        FilterChip(
                            selected = language == AppLanguage.ENGLISH,
                            onClick = { onLanguageChange(AppLanguage.ENGLISH) },
                            label = { Text("English") }
                        )
                        FilterChip(
                            selected = language == AppLanguage.ARABIC,
                            onClick = { onLanguageChange(AppLanguage.ARABIC) },
                            label = { Text("العربية") }
                        )
                    }
                }
            )
        }
    ) { padding ->
        Column(
            modifier = Modifier
                .fillMaxSize()
                .padding(padding)
                .verticalScroll(scrollState)
                .padding(32.dp),
            horizontalAlignment = Alignment.CenterHorizontally,
            verticalArrangement = Arrangement.spacedBy(24.dp)
        ) {
            // Hero Section
            Spacer(modifier = Modifier.height(20.dp))
            Text(strings.heroTitle, style = MaterialTheme.typography.displayLarge)
            Text(strings.heroSubtitle, style = MaterialTheme.typography.titleLarge)

            Row(horizontalArrangement = Arrangement.spacedBy(16.dp)) {
                Button(onClick = { onNavigate("signup") }) {
                    Text(strings.heroCtaGetStarted)
                }
                OutlinedButton(onClick = { onNavigate("privacy") }) {
                    Text(strings.heroCtaPrivacy)
                }
            }

            Spacer(modifier = Modifier.height(4.dp))

            // Big Noticeable Google Play Store Button
            Button(
                onClick = {
                    window.open("https://play.google.com/store/apps/details?id=client.digital.onthecoffee", "_blank")
                },
                modifier = Modifier
                    .height(64.dp)
                    .widthIn(min = 340.dp),
                colors = ButtonDefaults.buttonColors(
                    containerColor = MaterialTheme.colorScheme.primary
                ),
                elevation = ButtonDefaults.buttonElevation(defaultElevation = 8.dp)
            ) {
                Row(
                    verticalAlignment = Alignment.CenterVertically,
                    horizontalArrangement = Arrangement.spacedBy(16.dp),
                    modifier = Modifier.padding(horizontal = 8.dp)
                ) {
                    Text("🎮", style = MaterialTheme.typography.headlineMedium)
                    Column(
                        horizontalAlignment = Alignment.Start,
                        verticalArrangement = Arrangement.Center
                    ) {
                        Text(
                            strings.btnGooglePlay,
                            style = MaterialTheme.typography.titleMedium,
                            color = MaterialTheme.colorScheme.onPrimary
                        )
                        Text(
                            strings.googlePlaySubtitle,
                            style = MaterialTheme.typography.bodySmall,
                            color = MaterialTheme.colorScheme.onPrimary.copy(alpha = 0.85f)
                        )
                    }
                }
            }

            HorizontalDivider(modifier = Modifier.padding(vertical = 20.dp))

            // Features Section
            Text(strings.featuresSectionTitle, style = MaterialTheme.typography.headlineMedium)
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.SpaceEvenly
            ) {
                FeatureCard(strings.feature1Title, strings.feature1Desc)
                FeatureCard(strings.feature2Title, strings.feature2Desc)
                FeatureCard(strings.feature3Title, strings.feature3Desc)
            }

            HorizontalDivider(modifier = Modifier.padding(vertical = 20.dp))

            // Site Map / Quick Links Section
            Text(strings.quickLinksTitle, style = MaterialTheme.typography.headlineMedium)
            Column(
                verticalArrangement = Arrangement.spacedBy(8.dp),
                horizontalAlignment = Alignment.CenterHorizontally
            ) {
                TextButton(onClick = {
                    window.open("https://play.google.com/store/apps/details?id=client.digital.onthecoffee", "_blank")
                }) {
                    Text("🎮 " + strings.btnGooglePlay)
                }
                TextButton(onClick = { onNavigate("home") }) { Text(strings.quickLinkHome) }
                TextButton(onClick = { onNavigate("privacy") }) { Text(strings.quickLinkPrivacy) }
                TextButton(onClick = { onNavigate("signin") }) { Text(strings.quickLinkSignIn) }
                TextButton(onClick = { onNavigate("signup") }) { Text(strings.quickLinkSignUp) }
                TextButton(onClick = { onNavigate("forgot") }) { Text(strings.quickLinkForgot) }
                TextButton(onClick = { onNavigate("delete") }) { Text(strings.quickLinkDelete) }
            }

            HorizontalDivider(modifier = Modifier.padding(vertical = 20.dp))

            // Footer
            Text(strings.copyright, style = MaterialTheme.typography.bodySmall)
            Text(strings.platformSupport, style = MaterialTheme.typography.bodySmall)
        }
    }
}

@Composable
fun FeatureCard(title: String, description: String) {
    Card(
        modifier = Modifier.width(280.dp).height(140.dp),
        elevation = CardDefaults.cardElevation(defaultElevation = 4.dp)
    ) {
        Column(
            modifier = Modifier.fillMaxSize().padding(16.dp),
            verticalArrangement = Arrangement.Center,
            horizontalAlignment = Alignment.CenterHorizontally
        ) {
            Text(title, style = MaterialTheme.typography.titleMedium)
            Spacer(modifier = Modifier.height(8.dp))
            Text(description, style = MaterialTheme.typography.bodyMedium)
        }
    }
}
